/** Query params on bounce URLs so the landing page can show a revoke toast after reload. */
export const ImpersonationBounceReasonParam = "reason";
export const ImpersonationBounceAccountParam = "account";

export const ImpersonationBounceReason = {
  AccessRevoked: "access-revoked",
  AccessEnded: "access-ended",
} as const;

export type ImpersonationBounceReasonValue =
  (typeof ImpersonationBounceReason)[keyof typeof ImpersonationBounceReason];

const BounceToastMessages: Record<ImpersonationBounceReasonValue, string> = {
  [ImpersonationBounceReason.AccessRevoked]:
    "Your access to this account has been revoked.",
  [ImpersonationBounceReason.AccessEnded]:
    "Your access to this account has ended.",
};

function formatBounceToastMessage(
  reason: ImpersonationBounceReasonValue,
  accountName: string | null
): string {
  const account = accountName?.trim();
  if (reason === ImpersonationBounceReason.AccessRevoked) {
    return account
      ? `Access to ${account} has been revoked.`
      : BounceToastMessages[reason];
  }
  if (reason === ImpersonationBounceReason.AccessEnded) {
    return account
      ? `Your access to ${account} has ended.`
      : BounceToastMessages[reason];
  }
  return BounceToastMessages[reason];
}

export function buildImpersonationBounceUrl(
  pathname: string,
  reason: ImpersonationBounceReasonValue,
  accountName?: string
): string {
  const params = new URLSearchParams({ [ImpersonationBounceReasonParam]: reason });
  const account = accountName?.trim();
  if (account) {
    params.set(ImpersonationBounceAccountParam, account);
  }
  return `${pathname}?${params.toString()}`;
}

export function getImpersonationBounceToastMessage(
  search: string
): string | null {
  const params = new URLSearchParams(search);
  const reason = params.get(
    ImpersonationBounceReasonParam
  ) as ImpersonationBounceReasonValue | null;
  if (!reason || !(reason in BounceToastMessages)) return null;
  return formatBounceToastMessage(reason, params.get(ImpersonationBounceAccountParam));
}

export function stripImpersonationBounceReason(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(ImpersonationBounceReasonParam);
  params.delete(ImpersonationBounceAccountParam);
  const next = params.toString();
  return next ? `?${next}` : "";
}
